// ============================================================================
// view.sows — the breeding-pig registry, read AND written locally
// ============================================================================
// /pig/sows rendered from SEVEN inputs and owned none of them. Six were
// app_store blobs reached through usePig() — ppp-breeders-v1 (its subject and
// the row its record sub-mode resolves by id), ppp-breeding-v1 and
// ppp-farrowing-v1 (the per-sow history), ppp-boars-v1 (the sire labels), and
// ppp-breed-options-v1 / ppp-origin-options-v1 (the two dropdowns) — and the
// seventh was the pig_dailys history off useDailysRecent(), which drives the
// SOWS/BOARS monthly feed panel. `pig.sows.core` owns all seven, which is what
// lets `sows` retire from the frozen app_store and pig_dailys consumer lists in
// tests/fixtures/former-eager-dependencies.json.
//
// THE TWO OPTION KEYS ARE NOT DEAD. Neither exists in the production app_store,
// which makes them look like leftovers. They are CREATED by this surface, the
// first time an operator adds a custom breed or origin — and
// tests/static/dead_hydration_removed_static.test.js pins their live boot
// hydration precisely because someone will eventually try to delete them.
// Reading them here is not a revival: it is the same conditional read the boot
// performs, with the same seed lists (now in src/lib/pig.ts) when the row is
// absent. The WRITER moved; the keys did not.
//
// AND ONE INPUT ARRIVED THROUGH A CHANNEL NO GUARD CAN SEE. `resolveSire` was a
// PigEntry prop whose body was an App closure over App's OWN breedingCycles and
// boarNames. The former-eager derivation scores hook destructures and prop
// NAMES; a closure is neither, so dropping the destructures alone would have
// made the retirement mechanically green while every sire label still read two
// former-eager boot values out of App. The page calls resolveFarrowingSire
// itself now, and the hand-off is GONE rather than retained-but-unused: App no
// longer builds the closure and PigEntry no longer threads it.
//
// THIS FILE USED TO SAY THE SURFACE WAS PARTIAL, AND WHY. It named
// `legacy.context.pig.breeders` over three App writes — `persistBreeders`
// (`sbSave('ppp-breeders-v1', …)`), `persistBreedOptions` and
// `persistOriginOptions` — "declared as ONE residual because they are one
// coupling", each called beside PigContext's raw setter. All three writers are
// retired together, which is the only way this id could ever have moved.
//
// WHY THE WRITES COULD MOVE. This file argued a route-local write "costs parity
// this page cannot pay": App's `sbSave` races each attempt against an 8s
// timeout, retries three times, NEVER throws, and drives the header's Saving /
// Saved / Error indicator through AuthContext, where `useRouteMutation` is
// `retry: false` and rejects. That objection was to a REWRITE, and it stands.
// It is not an objection to a MOVE. src/lib/appStoreBlobApi.ts now holds that
// body verbatim, App's `sbSave` is a two-line delegate to it, and this page
// calls the same function with its own three keys — so the parity is identity,
// not resemblance. src/lib/appStoreBlobApi.test.ts runs both implementations
// over a seven-scenario matrix and asserts the complete observable trace is
// equal, with a two-attempt control proving the comparison can fail. The
// destination is the one src/lib/breedingTransferApi.ts already uses to upsert
// ppp-breeders-v1 for view.weighinsessions, a `permanent` surface.
//
// The catalog objection also stands and is also unchanged: `app_store` is one
// physical table holding every blob, so a routeMutationCatalog entry would have
// to invalidate all 22 catalog resources that read it for writes that touch one
// key each.
//
// ONE MIRROR STAYED, AND THE RULE IS "DOES ANYTHING READ IT".
//
//   breeders        `setBreeders` STAYS. Re-derived by enumerating every
//                   `usePig()` destructure in src/: PastureMapView and
//                   PigFeedView no longer call usePig() at all, and
//                   PigBatchesView / PigBatchPage call it for form state only —
//                   so of the five readers this file used to name, ONE is left,
//                   and it is src/App.tsx's `triggerMaintenance` snapshot
//                   default, `breeders: next.breeders || breeders`. That value
//                   decides `active_groups` membership through
//                   `computeSubCurrentCount`, so dropping the mirror would let
//                   a later broiler-batches / layer-groups / webforms save
//                   republish the group dropdown from a pre-edit registry.
//                   Real, small, and avoidable by keeping four characters.
//
//   breedOptions    Their setters are GONE. Not one module in the tree
//   originOptions   destructures either value off `usePig()`; they were mirror
//                   writes with no reader, and keeping them in step kept them
//                   in step for nobody.
//
// WHY A KEPT MIRROR DOES NOT KEEP THIS SURFACE PARTIAL. `setBreeders` is
// PigContext's own setter, reached through `usePig()` — not an App prop, and
// not a value App's boot has to have loaded for the call to be correct. The
// page publishes an array it computed from `pig.sows.core`, so the mirror it
// writes is if anything MORE current than the boot value. `residualLegacy`
// names "a remaining dependency on legacy App boot or an App-owned context";
// writing INTO a context is not depending on one. view.webforms states the same
// rule for the same shape ("a setter is not a read"), and pig.batches.index
// keeps `setFeederGroups` on exactly this reasoning.
//
// A CORRECTION THIS FILE OWES ITS READER. It used to say dropping the mirror
// would "rebuild the anonymous weigh-in tag list", "a cross-domain,
// public-facing regression". The public half is false: migration 193 dropped
// `anon_read` on webform_config and revoked anon access, and
// anonymous_boundary_static pins that with a forward barrier for every
// migration >= 193. Every reader of these mirrors is AUTHENTICATED. Separately,
// `persistBreeders` never called `triggerMaintenance` and the route-local write
// does not either — so a transfer recorded here already leaves `active_groups`
// stale until some other save or the next cold boot republishes it. That gap is
// pre-existing and is not made better or worse by this change.
//
// WHAT THIS DECLARATION PROVES. Emptying `residualLegacy` says there is no
// remaining App-context dependency. The separate ledger row, strict path fence,
// boundary target, and renderer branch have now landed, so canonical
// `/pig/sows` routes receive their released claim.
//
// THE FENCE THIS FILE USED TO ASK FOR HAS LANDED: src/app/boot/pigSowsPath.ts,
// wired ahead of ledger membership and pinned there by
// tests/static/pig_sows_fence_static.test.js. It was needed because
// `/pig/sows/*` is a SPLAT row and `sows` has no `VIEW_VARIANTS` entry, so this
// one surface id answers for the whole space; unfenced with a synthetic ledger
// row, the real resolver released on all 18 adversarial shapes probed, 12 of
// them at a foreign pathname. Fenced, it admits the two shapes the component
// actually serves — the list and one decoded-safe record segment — and nothing
// else. The ledger row, boundary target pair, and renderer branch now preserve
// that strict mount contract.
//
// The FORM state (breederForm, showBreederForm, editBreederId, sowSearch) stays
// on usePig for the same reason it does on view.breeding and view.farrowing: it
// is UI state the provider happens to host, seeded from module-scope literals
// rather than from any boot read.
//
// THE RECORD SUB-MODE FAILS CLOSED, AND THE BRANCH ORDER IS LOAD-BEARING. This
// is one surface, not two (SURFACE_ROUTERS declares it `sub-mode`), and its
// record panel resolves a pig out of the same array the list renders. A pending
// or failed read resolves `find(...)` to undefined, so the loading and error
// branches sit ABOVE the not-found branch — otherwise every deep link would
// render "Breeding pig not found." while the registry was still in flight.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.sows',
  obligations: Object.freeze([Object.freeze({id: 'pig.sows.core', owner: 'permanent'})]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
